package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.Placeholders
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.textDirection
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LDangerIcon
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun PlaceholderText(
    value: String,
    modifier: Modifier = Modifier,
    warningPlaceholders: Set<String> = emptySet(),
    direction: String = "auto",
    /**
     * BCP-47 tag for the text inside, or null when it genuinely is not known.
     *
     * Load-bearing, not decoration: Han is unified in Unicode, so without this the browser picks
     * a Han face by document language and a Chinese reviewer reads Japanese glyph forms. See
     * FontStack and registerScriptFonts. Null renders no attribute and keeps today's behaviour.
     */
    lang: String? = null,
) {
    Div(
        modifier
            .then(if (looksLikeCode(value)) Modifier.monoFont() else Modifier)
            .toAttrs {
                attr("dir", direction)
                lang?.let { attr("lang", it) }
            },
    ) {
        val placeholders = dev.lingvo.shared.Placeholders.extract(value)
        var cursor = 0
        placeholders.forEach { placeholder ->
            val index = value.indexOf(placeholder, startIndex = cursor)
            if (index >= 0) {
                if (index > cursor) Text(value.substring(cursor, index))
                Span(
                    EditorPlaceholderStyle.toModifier()
                        .then(
                            if (placeholder in warningPlaceholders) QaUnderlineStyle.toModifier()
                            else Modifier,
                        )
                        .toAttrs(),
                ) {
                    Text(placeholder)
                }
                cursor = index + placeholder.length
            }
        }
        if (cursor < value.length) Text(value.substring(cursor))
        if (value.isEmpty()) Text("-")
    }
}

@Composable
internal fun InlineQaBlock(
    value: String,
    findings: List<InlineQaFinding>,
    modifier: Modifier = Modifier,
) {
    if (findings.isEmpty()) return
    Column(modifier.fillMaxWidth().gap(Space.SM)) {
        Row(QaReasonStyle.toModifier().flexWrap(FlexWrap.Wrap)) {
            findings.forEach { finding ->
                // **`key`, so a different finding is a different node.**
                //
                // Without it these are identified by slot, so as somebody types and the findings
                // change the badge at index 0 is *reused*: a DANGER finding replaced by a WARNING
                // one keeps the same element, and `LBadge`'s colour transition — which is correct
                // and deliberate where it was written, an MT receipt flipping Running→Done under a
                // watching eye — cross-fades red to amber under the caret instead. The system's
                // first rule is that nothing competing with typing animates.
                //
                // The announcement matters more than the colour. An ERROR badge carries
                // `role="alert"`; reusing its node for a different sentence, or quietly dropping
                // the role when the severity changes, is the same identity problem the editor's
                // live region already solves with `key(announcement.id)`.
                key(finding) {
                LBadge(
                    text = finding.message,
                    modifier = EditorQaFindingStyle.toModifier()
                        .attr("data-editor-qa-finding", "true")
                        .then(
                            if (finding.severity == QaSeverity.ERROR) {
                                Modifier.attr("role", "alert")
                            } else {
                                Modifier
                            },
                        ),
                    variant = if (finding.severity == QaSeverity.ERROR) {
                        LBadgeVariant.DANGER
                    } else {
                        LBadgeVariant.WARNING
                    },
                    // **The severity is a shape, not only a hue.** Both variants drew the same
                    // pill in two colours, and the message beneath a field never names its own
                    // severity ("3 characters over the limit", "Translation is empty."), so a
                    // translator with red-green deficiency could not tell which finding blocks
                    // approval. The glyph is the same pair the review lane uses for these two
                    // severities, and it is `aria-hidden` inside a badge that already reads out.
                    leading = {
                        if (finding.severity == QaSeverity.ERROR) LDangerIcon() else LWarningIcon()
                    },
                    // A finding is a sentence quoting the translator's own text, not a status word.
                    wrap = true,
                )
                }
            }
        }
    }
}

internal fun editorSaveStatus(draft: EditorDraft): String = when {
    draft.saving -> tr("editor.editor_text.saving", "Saving")
    draft.dirty -> tr("editor.editor_text.unsaved_changes", "Unsaved changes")
    else -> tr("editor.editor_text.saved", "Saved")
}

internal fun editorSaveConfirmation(approve: Boolean): String =
    if (approve) tr("editor.editor_text.approved", "Approved") else tr("editor.editor_text.saved", "Saved")

/**
 * What a screen reader hears when a save lands, as one sentence per verb.
 *
 * It was `"${editorSaveConfirmation(approve)} $keyName"` — a translated word glued to a key name by
 * Kotlin, which gives a translator two fragments and no way to say how they join. English happens
 * to put the verb first; plenty of languages do not, and none of them can express that through a
 * concatenation somebody else wrote. One key per verb, with the key name as a named argument, is
 * the whole sentence in one editable string.
 */
internal fun editorCompletionMessage(keyName: String, approve: Boolean): String =
    if (approve) {
        trf("editor.editor_text.approved_key", "Approved {key}", "key" to keyName)
    } else {
        trf("editor.editor_text.saved_key", "Saved {key}", "key" to keyName)
    }

/**
 * The language name riding on a Source / Target heading.
 *
 * `lang` on the span rather than on the whole heading: "Source" is interface English and "Deutsch"
 * is not, and a screen reader told the heading is German reads the word "Source" with a German
 * voice. The two are marked separately because they are two languages on one line.
 */
@Composable
private fun EditorFieldSectionLocale(name: String, locale: String) {
    Span(
        EditorFieldSectionLocaleStyle.toModifier().toAttrs {
            attr("data-editor-field-section-locale", "true")
            if (locale.isNotBlank()) attr("lang", locale)
        },
    ) {
        Text(name)
    }
}

@Composable
internal fun MutedText(value: String) {
    Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).toAttrs()) {
        Text(value)
    }
}

@Composable
internal fun EditorSingularFields(
    state: TranslationEditorState,
    row: KeyRow,
    draft: EditorDraft,
    findings: List<InlineQaFinding>,
    onCopySource: () -> Unit,
    onInsertText: (String) -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
    readOnly: Boolean = false,
) {
    val source = row.sourceText()
    val sourceDirection = textDirection(row.source.locale).html
    val qa = fieldQaPresentation(findings)
    val canWrite = state.capability.canWriteTranslations && !readOnly
    // **The sparkle is an MT affordance, so `canSpendCredits` decides whether it exists.**
    // `POST /mt/translate` checks the `mt:spend` scope *and* role >= ADMIN, so gating on write
    // alone drew this button for every translator and answered 403 on every press — the
    // offer-then-refuse shape spec §2.6 forbids. Absent, not disabled.
    val machineProvider = defaultMachineProvider(state.projectSlug, state.enabledProviders)
        .takeIf { canWrite && state.capability.canSpendCredits }
    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        // Source and target used to be visually indistinguishable blocks: both headings existed,
        // but were clipped to the accessibility tree. Keep the semantic H2 landmarks and make
        // them carry the two language axes people compare while translating. Tight spacing inside
        // each group and a larger gap between groups makes the relationship visible without adding
        // another card or border.
        Column(
            Modifier
                .fillMaxWidth()
                .gap(Space.SM)
                .attr("data-editor-field-section", "source"),
        ) {
            H2(
                EditorFieldSectionHeadingStyle.toModifier().toAttrs {
                    attr("data-editor-source-label", "true")
                },
            ) {
                Text(t("editor.editor_text.source", "Source"))
                EditorFieldSectionLocale(localeInfo(row.source.locale).englishName, row.source.locale)
            }
            EditorSourceField(
                value = source,
                direction = sourceDirection,
                onCopySource = onCopySource,
                localeLabel = row.source.locale,
                lang = row.source.locale,
                warningPlaceholders = Placeholders.validate(source, draft.value)
                    .map { it.placeholder }
                    .toSet(),
                machineProviderLabel = machineProvider?.displayName,
                onMachineTranslate = machineProvider?.let { provider ->
                    {
                        rememberMachineProvider(state.projectSlug, provider)
                        state.panels.translate(row, provider)
                    }
                },
                machineTranslating = machineProvider in state.panels.translatingProviders[row.key.id].orEmpty(),
            )
        }
        Column(
            Modifier
                .fillMaxWidth()
                .gap(Space.SM)
                .attr("data-editor-field-section", "target"),
        ) {
            H2(
                EditorFieldSectionHeadingStyle.toModifier().toAttrs {
                    attr("data-editor-target-label", "true")
                },
            ) {
                Text(t("editor.editor_text.target", "Target"))
                EditorFieldSectionLocale(localeInfo(state.locale).englishName, state.locale)
            }
            EditorTargetField(
                keyId = row.key.id,
                category = null,
                value = draft.value,
                onValueChange = state::updateValue,
                readOnly = !canWrite,
                label = null,
                accessibleLabel = t("editor.editor_text.target", "Target"),
                placeholder = if (canWrite) {
                    t("editor.editor_text.enter_translation", "Enter translation...")
                } else {
                    t("editor.editor_text.no_translation_yet", "No translation yet")
                },
                error = qa.error,
                qaFindings = findings,
                codeLike = looksLikeCode(source),
                lang = state.locale,
            )
            if (canWrite) {
                EditorFieldToolbar(
                    state = state,
                    row = row,
                    draft = draft,
                    onInsertText = onInsertText,
                )
            }
            EditorBudgetMeter(value = draft.value, maxLength = row.key.maxLength)
            InlineQaBlock(draft.value, qa.inlineFindings)
        }
    }
}

/**
 * Width of the category label column in the plural stack — narrow on purpose. The label and its
 * plain-language example ("Few — 2–4 items") can wrap onto a second line without costing anything:
 * row height is set by the field column beside it, which is taller in every real case.
 */
private const val EDITOR_PLURAL_LABEL_COLUMN_PX = 128

@Composable
internal fun EditorPluralFields(
    state: TranslationEditorState,
    row: KeyRow,
    draft: EditorDraft,
    findings: List<InlineQaFinding>,
    onCopySource: () -> Unit,
    onInsertText: (String) -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
    readOnly: Boolean = false,
) {
    val presentation = editorPluralPanels(row, draft, state.locale, findings)
    val categories = presentation.requiredPanels.map { it.category }
    val sourceDirection = textDirection(row.source.locale).html
    val canWrite = state.capability.canWriteTranslations && !readOnly
    // Gated on spend authority for the same reason the singular column is: `POST /mt/translate`
    // refuses anyone below ADMIN, and a plural key drew this button six times over.
    val machineProvider = defaultMachineProvider(state.projectSlug, state.enabledProviders)
        .takeIf { canWrite && state.capability.canSpendCredits }
    Column(Modifier.fillMaxWidth().gap(Space.LG)) {
        Span(EditorPluralCompletionStyle.toModifier().toAttrs()) {
            Text(pluralCompletionLabel(categories, draft))
        }
        // The action row and the punctuation toolbar act on the caret, not on any one form, so
        // they render once here instead of once per category below. A plural key used to mount
        // both inside the loop — six identical "Translate with <provider>" buttons on an Arabic
        // key, all firing the same row-level request (EditorPanelStore.translate takes the whole
        // KeyRow) and all six spinning together, plus six punctuation toolbars each reporting
        // whichever form the caret happened to be in rather than their own.
        Column(
            Modifier
                .fillMaxWidth()
                .gap(Space.SM)
                .attr("data-editor-plural-tools", "true"),
        ) {
            EditorSourceActionRow(
                onCopySource = onCopySource,
                localeLabel = row.source.locale,
                machineProviderLabel = machineProvider?.displayName,
                onMachineTranslate = machineProvider?.let { provider ->
                    {
                        rememberMachineProvider(state.projectSlug, provider)
                        state.panels.translate(row, provider)
                    }
                },
                machineTranslating = machineProvider in state.panels.translatingProviders[row.key.id].orEmpty(),
            )
            if (canWrite) {
                EditorFieldToolbar(
                    state = state,
                    row = row,
                    draft = draft,
                    onInsertText = onInsertText,
                )
            }
        }
        EditorPluralUnit(
            categories = categories,
            selected = state.pluralCategory,
            onSelected = { state.pluralCategory = it },
        ) { category ->
            val panel = presentation.requiredPanels.first { it.category == category }
            val qa = fieldQaPresentation(panel.findings)
            // The category in a narrow left column, the field beside it: comparing товар / товара
            // / товаров means seeing every form on one screen (docs/DESIGN.md:359-364), which a
            // ~421px bordered card per form never allowed — four Russian forms did not fit one
            // viewport. The source is now a caption under the field rather than a card with its
            // own button row (those buttons live in the shared row above), and the preview is a
            // caption rather than a bordered box.
            Row(Modifier.fillMaxWidth().minWidth(0.px).gap(Space.MD)) {
                Column(
                    Modifier
                        .width(EDITOR_PLURAL_LABEL_COLUMN_PX.px)
                        .gap(Space.XXS)
                        .styleModifier { property("flex", "0 0 auto") },
                ) {
                    H3(EditorSectionHeadingStyle.toModifier().toAttrs()) {
                        Text(panel.categoryLabel)
                        // The separating space sits outside the example span on purpose. The span
                        // is `white-space: nowrap` so a long range never splits across lines, and
                        // a space *inside* that run stops being a break opportunity — the label
                        // then has none at all and overflows the fixed column instead of wrapping.
                        // Out here it still breaks, so a too-long label wraps whole: "Many" then
                        // "— 11–99 items".
                        Text(" ")
                        Span(EditorPluralExampleStyle.toModifier().toAttrs()) {
                            Text(tf("editor.editor_text.example_dash", "— {example}", "example" to panel.example))
                        }
                    }
                    // A required form that is still empty is the thing DESIGN.md asks to be
                    // marked inline; a finished form says nothing further here.
                    if (panel.target.isBlank()) {
                        Span(EditorPluralStatusStyle.toModifier().toAttrs()) {
                            Text(tf(
                                "editor.editor_text.required_for_language_empty",
                                "Required for {language}, empty.",
                                "language" to localeInfo(state.locale).englishName,
                            ))
                        }
                    }
                }
                Column(
                    Modifier
                        .fillMaxWidth()
                        .minWidth(0.px)
                        .gap(Space.XS)
                        .styleModifier { property("flex", "1 1 auto") },
                ) {
                    EditorTargetField(
                        keyId = row.key.id,
                        category = category,
                        value = panel.target,
                        onValueChange = { state.updatePlural(category, it) },
                        readOnly = !canWrite,
                        label = null,
                        accessibleLabel = panel.categoryLabel,
                        // The form's name is the sentence's subject, and both halves go through
                        // the catalogue. It used to be a Kotlin template over
                        // `categoryLabel.lowercase()` — an English enum name inside an English
                        // frame the extractor could never reach — so every empty plural form in the
                        // product's core screen read "Enter the other form..." in every locale.
                        // `.lowercase()` is gone with it: a German label is a noun and stays
                        // capitalised.
                        placeholder = if (canWrite) {
                            tf(
                                "editor.editor_text.enter_the_form_form",
                                "Enter the {form} form…",
                                "form" to panel.categoryLabel,
                            )
                        } else {
                            tf(
                                "editor.editor_text.no_form_translation_yet",
                                "No {form} translation yet",
                                "form" to panel.categoryLabel,
                            )
                        },
                        error = qa.error,
                        qaFindings = panel.findings,
                        codeLike = looksLikeCode(panel.source),
                        lang = state.locale,
                    )
                    // The per-form source: a single truncatable reference line rather than its own
                    // bordered card. The full text is still one hover/select away in the DOM; what
                    // it gives up is the scroll well and button row the shared row above replaces.
                    PlaceholderText(
                        value = panel.source,
                        modifier = PluralSourceCaptionStyle.toModifier()
                            .fillMaxWidth()
                            .attr("data-editor-plural-source", "true"),
                        warningPlaceholders = Placeholders.validate(panel.source, panel.target)
                            .map { it.placeholder }
                            .toSet(),
                        direction = sourceDirection,
                        lang = row.source.locale,
                    )
                    // Per form, because the budget is a property of the control the form lands in
                    // and every category lands in the same one. The QA check counts each form
                    // separately too, so a meter measuring the joined value would disagree with
                    // the finding printed under it.
                    EditorBudgetMeter(value = panel.target, maxLength = row.key.maxLength)
                    // **Only where there is something to preview.** `pluralPreview` returns prose
                    // for a blank form, and concatenating a sample count with it printed
                    // "1 → Add this form to see a live preview." — a sentence that reads as though
                    // the number were part of the instruction. On a fresh Russian key that was four
                    // of them at once. The left column's "Required for …, empty." already says the
                    // form is missing, and it disappears on the same keystroke this appears on.
                    if (panel.target.isNotBlank()) {
                        Div(
                            PluralPreviewStyle.toModifier()
                                .attr("data-editor-plural-preview", "true")
                                .toAttrs(),
                        ) {
                            // A real arrow, not "->". Every other arrow in the product is "→", and
                            // this line sits under a field a translator reads all day.
                            Text(tf(
                                "editor.editor_text.sample_to_preview",
                                "{count} → {preview}",
                                "count" to category.sampleCount(),
                                "preview" to panel.preview,
                            ))
                        }
                    }
                    InlineQaBlock(panel.target, qa.inlineFindings)
                }
            }
        }
        SourceOnlyPluralDisclosure(
            panels = presentation.sourceOnlyPanels,
            direction = sourceDirection,
        )
    }
}
